import { animate } from "motion/mini";

/**
 * Reveals a screen change with the **Iris** effect: a circle growing from a
 * point uncovering the new screen over the old one.
 *
 * Used when the storefront first appears after "Carregando..." once merchant
 * appearance is known. Without it the cut is hard. It is the ONLY load
 * animation on purpose — skeleton→content is content swap inside the same face.
 * Same effect as the portal theme iris, but origin is {@link viewportTopLeft}
 * (no click). Uses View Transitions for two page snapshots and motion/mini
 * WAAPI `animate` with `pseudoElement` for `::view-transition-new`.
 */

/** Iris origin in viewport coordinates (px). */
export interface IrisOrigin {
  x: number;
  y: number;
}

/**
 * Flag on `<html>` while the transition runs — kills live CSS transitions
 * during the swap (see `globals.css`). Without it the new snapshot is taken
 * right after update while `transition-colors` still shows OLD values, so the
 * iris reveals the previous screen.
 */
const RUNNING_ATTR = "data-iris-transition";

/**
 * In-flight transition count. The browser ABANDONS the previous when a new one
 * starts and the abandoned `finished` rejects — so the FIRST cleanup would run
 * mid-SECOND animation; a raw `removeAttribute` would unfreeze CSS mid-effect.
 * Only the last to finish clears the flag.
 */
let runningTransitions = 0;

function markRunning(root: HTMLElement): void {
  runningTransitions += 1;
  root.setAttribute(RUNNING_ATTR, "");
}

function unmarkRunning(root: HTMLElement): void {
  runningTransitions = Math.max(0, runningTransitions - 1);
  if (runningTransitions === 0) {
    root.removeAttribute(RUNNING_ATTR);
  }
}

/**
 * How long the iris takes to cross the screen.
 *
 * Below ~0.35s the eye loses the circle and sees a flash — the jolt this effect
 * exists to remove. Exported because two implementations (this and
 * `initial-loading.ts`) must share the same duration to read as one effect.
 */
export const IRIS_DURATION_S = 1;

/**
 * Pure `easeOut`: iris must leave the origin fast (else the first third is a
 * tiny circle that reads as delay) and slow at the edge. `easeInOut` does the
 * opposite on both ends.
 */
export const IRIS_EASE = [0.22, 1, 0.36, 1] as const;

/**
 * `pseudoElement` is supported at runtime by motion's WAAPI animator but public
 * `AnimationOptions` still omit it. Widen HERE in one named place instead of
 * scattering `as any` at call sites.
 */
type PseudoElementOptions = Parameters<typeof animate>[2] & {
  pseudoElement?: string;
};

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function supportsViewTransitions(): boolean {
  return (
    typeof document !== "undefined" &&
    typeof document.startViewTransition === "function"
  );
}

/**
 * Radius from origin to the FARTHEST viewport corner. Full diagonal (or a magic
 * large number) keeps growing after cover; the exact distance ends when the
 * circle hits the edge.
 */
export function radiusToFarthestCorner({ x, y }: IrisOrigin): number {
  const { innerWidth: width, innerHeight: height } = window;
  return Math.hypot(Math.max(x, width - x), Math.max(y, height - y));
}

/**
 * Storefront origin: viewport TOP-LEFT.
 *
 * Portal iris starts from the clicked control; here it starts from load end —
 * no gesture. From top-left the reveal sweeps diagonally in reading order;
 * a center-born circle pushes every direction at once.
 */
export function viewportTopLeft(): IrisOrigin {
  return { x: 0, y: 0 };
}

/**
 * Runs `apply` under a view transition, animating the iris from `origin`.
 *
 * `apply` MUST be sync and leave the DOM final — it runs between snapshots. In
 * React wrap `setState` in `flushSync` or the new snapshot is still the OLD
 * screen. Falls back to an instant swap without View Transitions or when
 * reduced motion is preferred.
 */
export function runIrisTransition(
  origin: IrisOrigin,
  apply: () => void,
): void {
  if (!supportsViewTransitions() || prefersReducedMotion()) {
    apply();
    return;
  }

  const root = document.documentElement;
  const radius = radiusToFarthestCorner(origin);

  const transition = document.startViewTransition(() => {
    markRunning(root);
    apply();
  });

  void transition.ready
    .then(() => {
      animate(
        root,
        {
          clipPath: [
            `circle(0px at ${origin.x}px ${origin.y}px)`,
            `circle(${radius}px at ${origin.x}px ${origin.y}px)`,
          ],
        },
        {
          duration: IRIS_DURATION_S,
          ease: IRIS_EASE,
          pseudoElement: "::view-transition-new(root)",
        } satisfies PseudoElementOptions as PseudoElementOptions,
      );
    })
    .catch(() => {});

  void transition.finished
    .finally(() => {
      unmarkRunning(root);
    })
    .catch(() => {});
}
